// BUILTIN_CLASS_EXTENSIONS_FOCUS_V2
import { getEffectiveToday, isOpenEverything } from '../../utils/systemTime';
import { useState, useEffect } from 'react';
import { useStudentDataRefreshTick } from '../../utils/studentDataRefresh';
import { useNavigate } from 'react-router-dom';
import StudentSyllabus from './course/StudentSyllabus';
import StudentPd from './course/StudentPd';
import StudentCq from './course/StudentCq';
import StudentSelfEval from './course/StudentSelfEval';
import StudentDocuments from './course/StudentDocuments';
import StudentReadings from './course/StudentReadings';
import StudentAssignments from './course/StudentAssignments';
import StudentSchedule from './course/StudentSchedule';
import StudentInstructor from './course/StudentInstructor';
import Chat from './chat/Chat';
import VotingDisplay from './VotingDisplay';
import StudentProgress from './course/StudentProgress';
import StudentGroups from './course/StudentGroups';
import StudentQi from './course/StudentQi';
import StudentCheckIn from './StudentCheckIn';
import { isNativeApp } from '../../utils/nativeApp';
import CoursePlacement
  from '../../components/CoursePlacement';
import {
  loadStudentStructuredFormCheckpoints
} from '../../utils/structuredFormStudentTiming';
import {
  effectiveClassroomForWeek
} from '../../utils/classroomResolution';

import {
  fetchVisibleStudentNotifications
} from '../../utils/studentNotifications';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


async function fetchCourse(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/courses?id=eq.${courseId}&select=*,course_titles(name),semesters(name,start_date)`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  const data = await response.json();
  return data[0] || null;
}


async function fetchEnrolment(studentId, courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?student_id=eq.${studentId}&course_id=eq.${courseId}&active=eq.true&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  const data = await response.json();
  return data[0] || null;
}


function enrolmentHasCourseGroup(
  enrolment
) {
  return Boolean(
    enrolment &&

    enrolment.stream !== null &&
    enrolment.stream !== undefined &&
    enrolment.stream !== '' &&

    enrolment.group_number !== null &&
    enrolment.group_number !== undefined &&
    enrolment.group_number !== ''
  );
}


async function fetchSeriesStudentContexts(
  currentCourse,
  currentEnrolment,
  studentId,
  isMaster
) {
  if (
    !currentCourse?.course_series_id
  ) {
    return currentCourse
      ? [{
          course: currentCourse,
          enrolment:
            currentEnrolment || null
        }]
      : [];
  }

  const coursesResponse =
    await fetch(
      `${SUPABASE_URL}/rest/v1/courses?course_series_id=eq.${encodeURIComponent(
        currentCourse.course_series_id
      )}&select=*,course_titles(name),semesters(name,start_date)`,
      {
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization':
            'Bearer ' +
            SUPABASE_KEY
        }
      }
    );

  if (!coursesResponse.ok) {
    const detail =
      await coursesResponse.text();

    throw new Error(
      `Could not load linked course semesters (${coursesResponse.status}): ${detail}`
    );
  }

  const courses =
    await coursesResponse.json();

  const courseIds =
    (Array.isArray(courses)
      ? courses
      : [])
      .map(course => course.id)
      .filter(Boolean);

  if (!courseIds.length) {
    return [{
      course: currentCourse,
      enrolment:
        currentEnrolment || null
    }];
  }

  const enrolmentsResponse =
    await fetch(
      `${SUPABASE_URL}/rest/v1/enrolments?student_id=eq.${encodeURIComponent(
        studentId
      )}&course_id=in.(${courseIds.join(',')})&select=*`,
      {
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization':
            'Bearer ' +
            SUPABASE_KEY
        }
      }
    );

  if (!enrolmentsResponse.ok) {
    const detail =
      await enrolmentsResponse.text();

    throw new Error(
      `Could not load linked semester enrolments (${enrolmentsResponse.status}): ${detail}`
    );
  }

  const enrolments =
    await enrolmentsResponse.json();

  const enrolmentByCourse =
    new Map(
      (Array.isArray(enrolments)
        ? enrolments
        : [])
        .map(row => [
          String(row.course_id),
          row
        ])
    );

  if (
    currentEnrolment &&
    currentCourse?.id
  ) {
    enrolmentByCourse.set(
      String(currentCourse.id),
      currentEnrolment
    );
  }

  const today =
    getEffectiveToday();

  return (Array.isArray(courses)
    ? courses
    : [])
    .filter(seriesCourse => {
      const linkedEnrolment =
        enrolmentByCourse.get(
          String(seriesCourse.id)
        ) || null;

      if (
        !isMaster &&
        !linkedEnrolment
      ) {
        return false;
      }

      if (
        String(seriesCourse.id) ===
        String(currentCourse.id)
      ) {
        return true;
      }

      if (isMaster) {
        return true;
      }

      if (seriesCourse.is_live) {
        return true;
      }

      const startDate =
        seriesCourse
          ?.semesters
          ?.start_date ||
        '';

      return (
        !!startDate &&
        startDate <= today
      );
    })
    .sort((a, b) =>
      String(
        a?.semesters?.start_date ||
        ''
      ).localeCompare(
        String(
          b?.semesters?.start_date ||
          ''
        )
      )
    )
    .map(seriesCourse => ({
      course: seriesCourse,
      enrolment:
        enrolmentByCourse.get(
          String(seriesCourse.id)
        ) || null
    }));
}


async function fetchOnboarding(studentId, courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/student_course_onboarding?student_id=eq.${studentId}&course_id=eq.${courseId}&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  const data = await response.json();
  return data[0] || null;
}


async function fetchCourseHasSyllabus(courseId) {
  const [
    sectionsResponse,
    descriptionsResponse
  ] = await Promise.all([
    fetch(
      SUPABASE_URL +
        '/rest/v1/course_syllabus_sections' +
        '?course_id=eq.' +
        encodeURIComponent(
          courseId
        ) +
        '&select=course_id&limit=1',
      {
        headers: {
          'apikey':
            SUPABASE_KEY,

          'Authorization':
            'Bearer ' +
            SUPABASE_KEY
        }
      }
    ),

    fetch(
      SUPABASE_URL +
        '/rest/v1/course_assignment_type_descriptions' +
        '?course_id=eq.' +
        encodeURIComponent(
          courseId
        ) +
        '&select=course_id&limit=1',
      {
        headers: {
          'apikey':
            SUPABASE_KEY,

          'Authorization':
            'Bearer ' +
            SUPABASE_KEY
        }
      }
    )
  ]);

  if (
    !sectionsResponse.ok
  ) {
    throw new Error(
      'Loading syllabus sections failed (' +
      sectionsResponse.status +
      ')'
    );
  }

  if (
    !descriptionsResponse.ok
  ) {
    throw new Error(
      'Loading syllabus assignment descriptions failed (' +
      descriptionsResponse.status +
      ')'
    );
  }

  const [
    sections,
    descriptions
  ] = await Promise.all([
    sectionsResponse.json(),
    descriptionsResponse.json()
  ]);

  return (
    (
      Array.isArray(
        sections
      ) &&
      sections.length > 0
    ) ||
    (
      Array.isArray(
        descriptions
      ) &&
      descriptions.length > 0
    )
  );
}


async function fetchNextDates(courseId) {
  const today = getEffectiveToday();

  const [weeksResponse, allWeeksResponse] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&class_date=gte.${today}&select=id,week_number,class_date,cq_enabled,pd_enabled,attendance_enabled,qi_enabled,start_time_override,classroom_override&order=class_date.asc`,
      { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&qi_enabled=eq.true&select=id&limit=1`,
      { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
    )
  ]);

  const weeks = await weeksResponse.json();
  const qiWeeks = await allWeeksResponse.json();

  const [
    assignmentsResponse,
    typeSettingsResponse
  ] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/course_assignments?course_id=eq.${courseId}&select=*,assignment_types(name,requires_submission,is_core)&order=due_date.asc`,
      { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?course_id=eq.${courseId}&select=assignment_type_id,menu_order,display_name,group_deadlines_enabled,sequence_id,schedule_enabled,schedule_choice_enabled`,
      { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
    )
  ]);

  const assignments = await assignmentsResponse.json();
   const assignmentTypeSettings =
    await typeSettingsResponse.json();

  const sequencesResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_sequences?course_id=eq.${courseId}&select=id,course_id,name&order=created_at.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );

  const assignmentSequences =
    await sequencesResponse.json();

  return {
    nextWeek: weeks[0] || null,
    upcomingWeeks: Array.isArray(weeks) ? weeks : [],
    assignments,
    assignmentTypeSettings:
      Array.isArray(assignmentTypeSettings)
        ? assignmentTypeSettings
        : [],
    assignmentSequences:
      Array.isArray(assignmentSequences)
        ? assignmentSequences
        : [],
    hasQiWeeks: qiWeeks.length > 0
  };
}


async function fetchStudentAttendanceSchedule(
  enrolmentId,
  courseId
) {
  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization':
      'Bearer ' + SUPABASE_KEY
  };

  const membershipResponse =
    await fetch(
      `${SUPABASE_URL}/rest/v1/attendance_group_memberships?enrolment_id=eq.${enrolmentId}&select=attendance_group_id,attendance_groups!inner(course_id)&attendance_groups.course_id=eq.${courseId}&limit=1`,
      { headers }
    );

  const memberships =
    await membershipResponse.json();

  const membership =
    Array.isArray(memberships)
      ? memberships[0]
      : null;

  if (
    !membership
      ?.attendance_group_id
  ) {
    return {
      enabledWeekIds: [],
      timeOverrides: {},
      roomOverrides: {}
    };
  }

  const weeksResponse =
    await fetch(
      `${SUPABASE_URL}/rest/v1/attendance_group_weeks?attendance_group_id=eq.${membership.attendance_group_id}&select=course_week_id,start_time_override,classroom_override`,
      { headers }
    );

  const rows =
    await weeksResponse.json();

  const enabledWeekIds = [];

  const timeOverrides = {};
  const roomOverrides = {};

  (
    Array.isArray(rows)
      ? rows
      : []
  ).forEach(row => {
    if (!row.course_week_id) {
      return;
    }

    const weekId =
      String(
        row.course_week_id
      );

    enabledWeekIds.push(
      weekId
    );

    if (
      row.start_time_override
    ) {
      timeOverrides[
        weekId
      ] = String(
        row.start_time_override
      ).slice(0, 5);
    }

    if (
      String(
        row.classroom_override ||
        ''
      ).trim()
    ) {
      roomOverrides[
        weekId
      ] = String(
        row.classroom_override
      ).trim();
    }
  });

  return {
    enabledWeekIds,
    timeOverrides,
    roomOverrides
  };
}



async function fetchStudentAssignmentTiming(
  enrolmentId,
  courseId
) {
  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };

  const [
    membershipsResponse,
    deadlinesResponse,
    submissionsResponse
  ] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/assignment_group_memberships?enrolment_id=eq.${enrolmentId}&select=course_assignment_id,group_number`,
      { headers }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/course_assignment_group_deadlines?select=course_assignment_id,group_number,open_date,due_date,course_assignments!inner(course_id)&course_assignments.course_id=eq.${courseId}`,
      { headers }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/assignment_submissions?enrolment_id=eq.${enrolmentId}&course_assignment_schedule_item_id=is.null&structured_form_instance_id=is.null&select=course_assignment_id,extension_days`,
      { headers }
    )
  ]);

  const memberships =
    await membershipsResponse.json();
  const deadlines =
    await deadlinesResponse.json();
  const submissions =
    await submissionsResponse.json();

  return {
    memberships:
      Array.isArray(memberships)
        ? memberships
        : [],
    deadlines:
      Array.isArray(deadlines)
        ? deadlines
        : [],
    submissions:
      Array.isArray(submissions)
        ? submissions
        : []
  };
}


function addDaysToDateString(dateStr, days) {
  if (!dateStr || !days) return dateStr || null;

  const parts = String(dateStr)
    .slice(0, 10)
    .split('-')
    .map(Number);

  if (
    parts.length !== 3 ||
    !parts.every(Number.isFinite)
  ) {
    return dateStr;
  }

  const date = new Date(
    parts[0],
    parts[1] - 1,
    parts[2],
    12,
    0,
    0
  );

  date.setDate(
    date.getDate() + Number(days)
  );

  return [
    date.getFullYear(),
    String(date.getMonth() + 1)
      .padStart(2, '0'),
    String(date.getDate())
      .padStart(2, '0')
  ].join('-');
}


function applyStudentAssignmentTiming(
  assignments,
  assignmentTypeSettings,
  timing
) {
  const settingsByType = new Map(
    (assignmentTypeSettings || []).map(
      row => [
        String(row.assignment_type_id),
        row
      ]
    )
  );

  const membershipByAssignment =
    new Map(
      (timing?.memberships || []).map(
        row => [
          String(row.course_assignment_id),
          row
        ]
      )
    );

  const deadlineByAssignmentAndGroup =
    new Map(
      (timing?.deadlines || []).map(
        row => [
          `${row.course_assignment_id}|${row.group_number}`,
          row
        ]
      )
    );

  const submissionByAssignment =
    new Map(
      (timing?.submissions || []).map(
        row => [
          String(row.course_assignment_id),
          row
        ]
      )
    );

  return (assignments || []).map(
    assignment => {
      const settings =
        settingsByType.get(
          String(
            assignment.assignment_type_id
          )
        );

      const effectiveSequenceId =
        settings?.sequence_id || null;

      const requiresSubmission =
        assignment.assignment_types
          ?.requires_submission ?? true;

      if (!requiresSubmission) {
        return {
          ...assignment,
          effective_sequence_id:
            effectiveSequenceId,
          student_assignment_timing_resolved:
            true,
          student_assignment_applicable:
            true,
          effective_open_date: null,
          effective_due_date: null
        };
      }

      const usesGroupDeadlines =
        settings?.group_deadlines_enabled ===
          true &&
        assignment.groups_enabled === true;

      let applicable = true;
      let openDate =
        assignment.open_date || null;
      let dueDate =
        assignment.due_date || null;

      if (usesGroupDeadlines) {
        const membership =
          membershipByAssignment.get(
            String(assignment.id)
          );

        if (!membership) {
          applicable = false;
          openDate = null;
          dueDate = null;
        } else {
          const deadline =
            deadlineByAssignmentAndGroup.get(
              `${assignment.id}|${membership.group_number}`
            );

          openDate =
            deadline?.open_date || null;
          dueDate =
            deadline?.due_date || null;
        }
      }

      if (applicable && dueDate) {
        const submission =
          submissionByAssignment.get(
            String(assignment.id)
          );

        const extensionDays =
          Math.max(
            0,
            Number(
              submission?.extension_days
            ) || 0
          );

        if (extensionDays > 0) {
          dueDate =
            addDaysToDateString(
              dueDate,
              extensionDays
            );
        }
      }

      return {
        ...assignment,
        effective_sequence_id:
          effectiveSequenceId,
        student_assignment_timing_resolved:
          true,
        student_assignment_applicable:
          applicable,
        effective_open_date: openDate,
        effective_due_date: dueDate
      };
    }
  );
}


function getStudentAssignmentDate(assignment) {
  if (
    assignment
      ?.student_assignment_applicable ===
    false
  ) {
    return null;
  }

  const requiresSubmission =
    assignment?.assignment_types
      ?.requires_submission ?? true;

  if (!requiresSubmission) {
    return assignment?.occurs_on || null;
  }

  if (
    assignment
      ?.student_assignment_timing_resolved
  ) {
    return (
      assignment.effective_due_date ||
      null
    );
  }

  return assignment?.due_date || null;
}


function getStudentAssignmentOpenDate(
  assignment
) {
  if (
    assignment
      ?.student_assignment_applicable ===
    false
  ) {
    return null;
  }

  if (
    assignment
      ?.student_assignment_timing_resolved
  ) {
    return (
      assignment.effective_open_date ||
      null
    );
  }

  return assignment?.open_date || null;
}


async function createOnboarding(studentId, courseId) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/student_course_onboarding`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates'
      },
      body: JSON.stringify({
        student_id: studentId,
        course_id: courseId
      })
    }
  );
}


function formatDate(dateStr) {
  if (!dateStr) return '';

  return new Date(dateStr).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'short'
  });
}


function Icon({ name, size = 18 }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true
  };

  const paths = {
    dashboard: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </>
    ),
    syllabus: (
      <>
        <path d="M6 3h9l3 3v15H6z" />
        <path d="M15 3v4h4M9 11h6M9 15h6" />
      </>
    ),
    schedule: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="1.5" />
        <path d="M7 3v4M17 3v4M3 10h18" />
      </>
    ),
    progress: (
      <>
        <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
      </>
    ),
    pd: (
      <>
        <path d="M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3z" />
        <path d="M8 4v16M11 9h5M11 13h5" />
      </>
    ),
    cq: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M9.5 9a2.7 2.7 0 1 1 4.4 2.1c-1.2.8-1.9 1.3-1.9 2.9M12 17.5h.01" />
      </>
    ),
    qi: (
      <>
        <path d="m14.5 5.5 4 4M4 20l4.5-1 10-10a2.8 2.8 0 0 0-4-4l-10 10z" />
        <path d="m8.5 15.5 3 3" />
      </>
    ),
    selfeval: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M5 21a7 7 0 0 1 14 0M17 4l2-2M7 4 5 2" />
      </>
    ),
    groups: (
      <>
        <circle cx="8" cy="9" r="3" />
        <circle cx="16" cy="9" r="3" />
        <path d="M3 20a5 5 0 0 1 10 0" />
        <path d="M11 20a5 5 0 0 1 10 0" />
      </>
    ),
    readings: (
      <>
        <path d="M4 5h6a3 3 0 0 1 2 2v12a3 3 0 0 0-2-2H4z" />
        <path d="M20 5h-6a3 3 0 0 0-2 2v12a3 3 0 0 1 2-2h6z" />
      </>
    ),
    documents: (
      <>
        <path d="M3 6h7l2 2h9v11H3z" />
      </>
    ),
    instructor: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M5 21a7 7 0 0 1 14 0" />
      </>
    ),
    chat: (
      <>
        <path d="M4 5.5h16v11H9l-5 4v-15Z" />
        <path d="M8 10h8M8 13h5" />
      </>
    ),
    voting: (
      <>
        <path d="M4 9h16v11H4zM8 9V5h8v4" />
        <path d="m9 14 2 2 4-4" />
      </>
    ),
    assignments: (
      <>
        <path d="M6 3h9l3 3v15H6z" />
        <path d="M15 3v4h4M9 12h6M9 16h4" />
      </>
    ),
    bell: (
      <>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
        <path d="M10 21h4" />
      </>
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    checkin: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m8.5 12 2.2 2.2 4.8-5" />
      </>
    ),
    logout: (
      <>
        <path d="M10 5H5v14h5" />
        <path d="M13 8l4 4-4 4M17 12H9" />
      </>
    ),
    back: <path d="m15 18-6-6 6-6M9 12h11" />
  };

  return <svg {...common}>{paths[name] || paths.syllabus}</svg>;
}


const ALL_MENU_ITEMS = [
  { key: 'syllabus', label: 'Syllabus', icon: 'syllabus' },
  { key: 'schedule', label: 'Teaching Schedule', icon: 'schedule' },
  { key: 'readings', label: 'Readings', icon: 'readings' },
  {
    key: 'progress',
    label: 'My Progress',
    icon: 'progress',
    toggle: 'course_progress_enabled'
  },
  { key: 'checkin', label: 'Check In', icon: 'checkin', toggle: 'attendance_enabled', nativeOnly: true },
  { key: 'pd', label: 'Philosophical Diary', icon: 'pd', toggle: 'pd_enabled' },
  { key: 'cq', label: 'Questions Assignment', icon: 'cq', toggle: 'cq_enabled' },
  { key: 'qi', label: 'Question Improvement', icon: 'qi', toggle: 'qi_enabled' },
  { key: 'selfeval', label: 'Self-Evaluation', icon: 'selfeval', toggle: 'self_eval_enabled' },
  { key: 'groups', label: 'Groups', icon: 'groups' },
  { key: 'documents', label: 'Documents', icon: 'documents' },
  { key: 'instructor', label: 'Instructor Information', icon: 'instructor' },
  {
    key: 'chat',
    label: 'Course Chat',
    icon: 'chat',
    toggle: 'chat_enabled'
  },
  { key: 'voting', label: 'Class Voting', icon: 'voting', toggle: 'voting_enabled' }
];

function classDayForDate(dateStr) {
  if (!dateStr) return '';

  return new Date(
    `${String(dateStr).slice(0, 10)}T12:00:00`
  ).toLocaleDateString(
    'en-GB',
    {
      weekday: 'long'
    }
  );
}


function normalClassTimeForDate(
  course,
  dateStr
) {
  const day =
    classDayForDate(
      dateStr
    );

  if (
    course?.class_times?.[
      day
    ]
  ) {
    return String(
      course.class_times[
        day
      ]
    ).slice(0, 5);
  }

  if (
    course?.class_time
  ) {
    return String(
      course.class_time
    ).slice(0, 5);
  }

  return '';
}


function getMenuItems(
  course,
  hasQiWeeks,
  hasStudentGroups
) {
  if (!course) return [];

  return ALL_MENU_ITEMS.filter(item => {
    if (item.toggle && course[item.toggle] === false) return false;
    if (item.nativeOnly && !isNativeApp()) return false;

    if (
      item.key === 'groups' &&
      !hasStudentGroups
    ) {
      return false;
    }

    if (
      item.key === 'qi' &&
      course.qi_enabled !== true &&
      !hasQiWeeks
    ) {
      return false;
    }

    return true;
  });
}


export default function StudentCourse({
  session,
  courseId,
  onBack,
  onLogout
}) {
  const navigate = useNavigate();

  const sectionMatch =
    window.location.pathname.match(
      /^\/student\/course\/[^/]+\/(.+)/
    );

  const section = sectionMatch ? sectionMatch[1] : null;
  const searchParams = new URLSearchParams(window.location.search);
  const selectedAssignmentTypeId = searchParams.get('typeId') || null;
  const selectedSequenceId = searchParams.get('sequenceId') || null;

  const [course, setCourse] = useState(null);
  const [enrolment, setEnrolment] = useState(null);
  const [
    seriesContexts,
    setSeriesContexts
  ] = useState([]);
  const [onboarding, setOnboarding] = useState(null);

  const [
    hasSyllabus,
    setHasSyllabus
  ] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [nextWeek, setNextWeek] = useState(null);
  const [nextClassWeek, setNextClassWeek] = useState(null);
  const [nextClassTime, setNextClassTime] = useState('');
  const [nextClassRoom, setNextClassRoom] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [
    structuredDashboardCheckpoints,
    setStructuredDashboardCheckpoints
  ] = useState([]);
  const [loading, setLoading] = useState(true);
  const [otherAssignments, setOtherAssignments] = useState([]);
  const [assignmentTypeSettings, setAssignmentTypeSettings] = useState([]);
  const [assignmentSequences, setAssignmentSequences] = useState([]);
  const [hasQiWeeks, setHasQiWeeks] = useState(false);

  const [
    hasStudentGroups,
    setHasStudentGroups
  ] = useState(false);
  const refreshTick = useStudentDataRefreshTick();

  const activeSection = section || null;


  function navigateTo(
    key,
    typeId = null,
    sequenceId = null
  ) {
    const base = `/student/course/${courseId}/${key}`;
    const params = new URLSearchParams();

    if (typeId) {
      params.set('typeId', typeId);
    }

    if (sequenceId) {
      params.set('sequenceId', sequenceId);
    }

    const query = params.toString();
    navigate(query ? `${base}?${query}` : base);
  }

  function navigateBack() {
    navigate(`/student/course/${courseId}`);
  }


  function switchSeriesOffering(
    nextCourseId
  ) {
    if (
      !nextCourseId ||
      String(nextCourseId) ===
        String(courseId)
    ) {
      return;
    }

    const suffix =
      activeSection
        ? `/${activeSection}`
        : '';

    navigate(
      `/student/course/${nextCourseId}${suffix}`
    );
  }


  useEffect(() => {
    async function load() {
      const [c, e, notifs, dates] = await Promise.all([
        fetchCourse(courseId),
        fetchEnrolment(session.studentId, courseId),
        fetchVisibleStudentNotifications(
          {
            studentId:
              session.studentId,

            isMaster:
              session.isMaster,

            webSessionToken:
              session.webSessionToken,

            masterSessionToken:
              session.masterSessionToken
          },
          [courseId],
          5
        ),
        fetchNextDates(courseId)
      ]);

      if (!e && !session.isMaster) {
        setLoading(false);
        navigate('/student', {
          replace: true
        });
        return;
      }

      setCourse(c);
      setEnrolment(e);

      let loadedHasStudentGroups =
        enrolmentHasCourseGroup(e);

      try {
        const linkedContexts =
          await fetchSeriesStudentContexts(
            c,
            e,
            session.studentId,
            !!session.isMaster
          );

        setSeriesContexts(
          linkedContexts.length
            ? linkedContexts
            : [{
                course: c,
                enrolment:
                  e || null
              }]
        );
      } catch (error) {
        console.error(
          'Loading linked course semesters failed:',
          error
        );

        setSeriesContexts([
          {
            course: c,
            enrolment:
              e || null
          }
        ]);
      }

      setNotifications(
        Array.isArray(notifs)
          ? notifs
          : []
      );

      setNextWeek(
        dates.nextWeek
      );

      const upcomingWeeks =
        Array.isArray(
          dates.upcomingWeeks
        )
          ? dates.upcomingWeeks
          : [];

      let resolvedNextClass =
        null;

      let resolvedNextClassTime =
        '';

      let resolvedNextClassRoom =
        '';

      let attendanceSchedule =
        null;

      if (
        c?.group_based_attendance ===
          true &&
        e
      ) {
        attendanceSchedule =
          await fetchStudentAttendanceSchedule(
            e.id,
            courseId
          );

        const requiredSet =
          new Set(
            (
              attendanceSchedule
                .enabledWeekIds ||
              []
            ).map(String)
          );

        resolvedNextClass =
          upcomingWeeks.find(
            week =>
              requiredSet.has(
                String(
                  week.id
                )
              )
          ) || null;

        if (resolvedNextClass) {
          const normalTime =
            normalClassTimeForDate(
              c,
              resolvedNextClass
                .class_date
            );

          resolvedNextClassTime =
            c?.variable_timings_enabled ===
              true
              ? String(
                  attendanceSchedule
                    .timeOverrides?.[
                      String(
                        resolvedNextClass
                          .id
                      )
                    ] ||
                  normalTime ||
                  ''
                ).slice(0, 5)
              : normalTime;
        }

      } else {
        resolvedNextClass =
          upcomingWeeks.find(
            week =>
              week
                .attendance_enabled ===
              true
          ) || null;

        if (resolvedNextClass) {
          const normalTime =
            normalClassTimeForDate(
              c,
              resolvedNextClass
                .class_date
            );

          resolvedNextClassTime =
            c?.variable_timings_enabled ===
              true
              ? String(
                  resolvedNextClass
                    .start_time_override ||
                  normalTime ||
                  ''
                ).slice(0, 5)
              : normalTime;
        }
      }

      if (resolvedNextClass) {
        resolvedNextClassRoom =
          effectiveClassroomForWeek(
            c,
            resolvedNextClass,
            attendanceSchedule
              ?.roomOverrides
              ?.[
                String(
                  resolvedNextClass.id
                )
              ] ||
              null
          );
      }

      setNextClassWeek(
        resolvedNextClass
      );

      setNextClassTime(
        resolvedNextClassTime
      );

      setNextClassRoom(
        resolvedNextClassRoom
      );

      let resolvedAssignments =
        Array.isArray(dates.assignments)
          ? dates.assignments
          : [];

      // Sequence membership belongs to the course assignment type, not to a
      // particular student's enrolment/timing record. Attach it unconditionally
      // so sequence collapsing also works for Master Student / any view where
      // there is no matching enrolment row.
      const sequenceByType = new Map(
        (Array.isArray(dates.assignmentTypeSettings)
          ? dates.assignmentTypeSettings
          : []
        ).map(row => [
          String(row.assignment_type_id),
          row.sequence_id || null
        ])
      );

      resolvedAssignments = resolvedAssignments.map(assignment => ({
        ...assignment,
        effective_sequence_id:
          sequenceByType.get(
            String(assignment.assignment_type_id)
          ) || null
      }));

      if (
        e &&
        resolvedAssignments.length > 0
      ) {
        const timing =
          await fetchStudentAssignmentTiming(
            e.id,
            courseId
          );

        if (
          Array.isArray(
            timing?.memberships
          ) &&
          timing.memberships.length > 0
        ) {
          loadedHasStudentGroups =
            true;
        }

        resolvedAssignments =
          applyStudentAssignmentTiming(
            resolvedAssignments,
            dates.assignmentTypeSettings,
            timing
          );
      }

      let structuredDashboardRows = [];

      if (
        e &&
        resolvedAssignments.length > 0
      ) {
        try {
          structuredDashboardRows =
            await loadStudentStructuredFormCheckpoints({
              courseId,
              enrolment: e,
              assignments:
                resolvedAssignments,
              assignmentTypeSettings:
                dates.assignmentTypeSettings,
              isMaster:
                !!session.isMaster
            });
        } catch (error) {
          console.error(
            'Loading Structured Form dashboard timing failed:',
            error
          );
        }
      }

      setAssignments(
        resolvedAssignments
      );

      setStructuredDashboardCheckpoints(
        structuredDashboardRows
      );

      setHasStudentGroups(
        loadedHasStudentGroups
      );

      setHasQiWeeks(dates.hasQiWeeks);
      setAssignmentTypeSettings(
        Array.isArray(dates.assignmentTypeSettings)
          ? dates.assignmentTypeSettings
          : []
      );
      setAssignmentSequences(
        Array.isArray(dates.assignmentSequences)
          ? dates.assignmentSequences
          : []
      );

      const seen = new Set();
      const uniqueTypes =
        resolvedAssignments
          .filter(a => !a.assignment_types?.is_core)
          .filter(a => {
            if (seen.has(a.assignment_type_id)) return false;
            seen.add(a.assignment_type_id);
            return true;
          });

      const menuOrderByType = new Map();

      (
        Array.isArray(dates.assignmentTypeSettings)
          ? dates.assignmentTypeSettings
          : []
      ).forEach(row => {
        const raw = row.menu_order;

        if (
          raw === null ||
          raw === undefined ||
          raw === ''
        ) {
          return;
        }

        const numeric = Number(raw);

        if (Number.isFinite(numeric)) {
          menuOrderByType.set(
            String(row.assignment_type_id),
            numeric
          );
        }
      });

      uniqueTypes.sort((a, b) => {
        const aOrder = menuOrderByType.get(
          String(a.assignment_type_id)
        );
        const bOrder = menuOrderByType.get(
          String(b.assignment_type_id)
        );

        const aHasOrder = Number.isFinite(aOrder);
        const bHasOrder = Number.isFinite(bOrder);

        if (aHasOrder && bHasOrder) {
          return aOrder - bOrder;
        }

        if (aHasOrder) return -1;
        if (bHasOrder) return 1;

        return 0;
      });

      setOtherAssignments(uniqueTypes);

      if (e) {
        const courseHasSyllabus =
          await fetchCourseHasSyllabus(
            courseId
          );

        setHasSyllabus(
          courseHasSyllabus
        );

        let ob = await fetchOnboarding(
          session.studentId,
          courseId
        );

        if (!ob) {
          await createOnboarding(
            session.studentId,
            courseId
          );

          ob = {
            syllabus_completed: false,
            contract_completed: false
          };
        }

        setOnboarding(ob);

        if (!session.isMaster) {
          if (
            courseHasSyllabus &&
            !ob.syllabus_completed
          ) {
            if (section !== 'syllabus') {
              navigate(
                `/student/course/${courseId}/syllabus`,
                { replace: true }
              );
            }
                    }
        }
      }

      setLoading(false);
    }

    load();
  }, [
  courseId,
  session.studentId,
  session.isMaster,
  session.webSessionToken,
  session.masterSessionToken,
  navigate,
  section,
  refreshTick
]);


  const courseName =
    course?.course_titles?.name ||
    course?.name ||
    'Course';


  function handleOnboardingUpdate(field) {
    setOnboarding(prev => ({
      ...prev,
      [field]: true
    }));

    if (field === 'syllabus_completed') {
      navigate(
        `/student/course/${courseId}`,
        { replace: true }
      );
    }
  }


  function getNextPdDue() {
    if (!course?.pd_due_day || !nextWeek) return null;

    const dayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday'
    ];

    const targetDay =
      dayNames.indexOf(course.pd_due_day);

    const classDate =
      new Date(nextWeek.class_date);

    const dueDate =
      new Date(classDate);

    while (dueDate.getDay() !== targetDay) {
      dueDate.setDate(dueDate.getDate() + 1);
    }

    return dueDate.toISOString().split('T')[0];
  }


  function getCqDueHoursBeforeClass() {
    const configuredHours = Number(
      course?.cq_due_hours_before_class ?? 1
    );

    return (
      Number.isFinite(configuredHours) &&
      configuredHours >= 0
    )
      ? configuredHours
      : 1;
  }


  function getNextCqDeadline() {
    if (!nextWeek?.class_date) return null;

    const dateParts = String(
      nextWeek.class_date
    )
      .slice(0, 10)
      .split('-')
      .map(Number);

    if (
      dateParts.length !== 3 ||
      !dateParts.every(Number.isFinite)
    ) {
      return null;
    }

    const classDayDate = new Date(
      dateParts[0],
      dateParts[1] - 1,
      dateParts[2]
    );

    const dayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday'
    ];

    const classDay =
      dayNames[classDayDate.getDay()];

    const resolvedTime =
      course?.class_times?.[classDay] ||
      course?.class_time ||
      '09:00';

    const timeParts = String(resolvedTime)
      .slice(0, 5)
      .split(':')
      .map(Number);

    const hour =
      Number.isFinite(timeParts[0])
        ? timeParts[0]
        : 9;

    const minute =
      Number.isFinite(timeParts[1])
        ? timeParts[1]
        : 0;

    const classStart = new Date(
      dateParts[0],
      dateParts[1] - 1,
      dateParts[2],
      hour,
      minute,
      0,
      0
    );

    return new Date(
      classStart.getTime() -
        getCqDueHoursBeforeClass() *
          60 *
          60 *
          1000
    );
  }

  function getUpcomingAssignments() {
    const today = getEffectiveToday();

    // Sequence stage order is based on the course's configured schedule.
    // Student-specific timing (for example an extension) can change the date
    // displayed to that student, but it should not cause a later scaffolded
    // stage to leapfrog an earlier stage in the sequence.
    function getScheduledAssignmentDate(assignment) {
      const requiresSubmission =
        assignment.assignment_types?.requires_submission ?? true;

      return requiresSubmission
        ? assignment.due_date || null
        : assignment.occurs_on || null;
    }

    const dashboardAssignments = [
      ...assignments.filter(
        assignment =>
          assignment
            ?.assignment_types
            ?.assignment_kind !==
          'structured_form'
      ),
      ...structuredDashboardCheckpoints
    ];

    const upcoming =
      dashboardAssignments
      .filter(assignment => {
        const effectiveDate =
          getStudentAssignmentDate(assignment);

        return (
          !!effectiveDate &&
          String(effectiveDate).slice(0, 10) >= today
        );
      })
      .sort((a, b) => {
        const aDate = String(
          getScheduledAssignmentDate(a) ||
            getStudentAssignmentDate(a) ||
            ''
        ).slice(0, 10);

        const bDate = String(
          getScheduledAssignmentDate(b) ||
            getStudentAssignmentDate(b) ||
            ''
        ).slice(0, 10);

        return aDate.localeCompare(bDate);
      });

    const nextBySequenceOrType = new Map();

    upcoming.forEach(assignment => {
      const sequenceId =
        assignment.effective_sequence_id;

      const key = sequenceId
        ? `sequence:${sequenceId}`
        : `type:${
            assignment.assignment_type_id ||
            assignment.id
          }`;

      const existing =
        nextBySequenceOrType.get(key);

      if (!existing) {
        nextBySequenceOrType.set(
          key,
          assignment
        );
        return;
      }

      const existingDate = String(
        getScheduledAssignmentDate(existing) ||
          getStudentAssignmentDate(existing) ||
          ''
      ).slice(0, 10);

      const candidateDate = String(
        getScheduledAssignmentDate(assignment) ||
          getStudentAssignmentDate(assignment) ||
          ''
      ).slice(0, 10);

      if (
        candidateDate &&
        (!existingDate || candidateDate < existingDate)
      ) {
        nextBySequenceOrType.set(
          key,
          assignment
        );
      }
    });

    return Array.from(
      nextBySequenceOrType.values()
    )
      .sort((a, b) => {
        const aDate = String(
          getScheduledAssignmentDate(a) ||
            getStudentAssignmentDate(a) ||
            ''
        ).slice(0, 10);

        const bDate = String(
          getScheduledAssignmentDate(b) ||
            getStudentAssignmentDate(b) ||
            ''
        ).slice(0, 10);

        return aDate.localeCompare(bDate);
      })
      .slice(0, 3);
  }


  function getSelfEvalInfo() {
    if (!course || course?.self_eval_enabled === false) {
      return [];
    }

    const today = getEffectiveToday();

    const configured = [
      {
        label: 'Mid-Term Self-Evaluation',
        opens: course.mid_eval_open_date,
        closes:
          addDaysToDateString(
            course.mid_eval_close_date,
            course.mid_eval_extension_days
          )
      },
      {
        label: 'Final Self-Evaluation',
        opens: course.final_eval_open_date,
        closes:
          addDaysToDateString(
            course.final_eval_close_date,
            course.final_eval_extension_days
          )
      }
    ]
      .filter(item => item.opens && item.closes)
      .filter(item => today <= item.closes)
      .map(item => ({
        ...item,
        status:
          today >= item.opens &&
          today <= item.closes
            ? 'open'
            : 'upcoming'
      }))
      .sort((a, b) => {
        if (a.status !== b.status) {
          return a.status === 'open' ? -1 : 1;
        }

        return a.opens.localeCompare(b.opens);
      });

    return configured;
  }


  if (loading) {
    return (
      <div className="vc-loading-shell">
        <style>{coursePortalCss}</style>
        <div className="vc-loading">Loading...</div>
      </div>
    );
  }


  const baseMenuItems =
  getMenuItems(
    course,
    hasQiWeeks,
    hasStudentGroups
  );

  const selfEvalIndex =
    baseMenuItems.findIndex(
      item => item.key === 'selfeval'
    );

  const insertIndex =
    selfEvalIndex >= 0
      ? selfEvalIndex
      : baseMenuItems.length;

  const settingByTypeId = new Map(
    assignmentTypeSettings.map(setting => [
      String(setting.assignment_type_id),
      setting
    ])
  );

  const sequenceById = new Map(
    assignmentSequences.map(sequence => [
      String(sequence.id),
      sequence
    ])
  );

  const seenSequenceIds = new Set();
  const assignmentNavigationItems = [];

  otherAssignments.forEach(assignment => {
    const typeId = assignment.assignment_type_id;

    if (!typeId) return;

    const setting = settingByTypeId.get(String(typeId));
    const sequenceId = setting?.sequence_id || null;

    if (sequenceId) {
      if (seenSequenceIds.has(String(sequenceId))) {
        return;
      }

      seenSequenceIds.add(String(sequenceId));

      const sequence = sequenceById.get(String(sequenceId));

      assignmentNavigationItems.push({
        key: 'assignments',
        label: sequence?.name || 'Assignment Sequence',
        icon: 'assignments',
        sequenceId
      });

      return;
    }

    assignmentNavigationItems.push({
      key: 'assignments',
      label:
        assignment.assignment_types?.name ||
        'Assignment',
      icon: 'assignments',
      assignmentTypeId: typeId
    });
  });

  const navigationItems = [
    ...baseMenuItems.slice(0, insertIndex),

    ...assignmentNavigationItems,

    ...baseMenuItems.slice(insertIndex)
  ];

  const onboardingInProgress =
    !session.isMaster &&
    hasSyllabus &&
    onboarding &&
    !onboarding.syllabus_completed;

  const visibleNavigationItems =
    onboardingInProgress
      ? [
          {
            key: 'syllabus',
            label: 'Syllabus',
            icon: 'syllabus'
          }
        ]
      : navigationItems;


  function renderSidebar() {
    return (
      <aside className="vc-sidebar">
        <div className="vc-brand">
          <button
            className="vc-back-courses"
            type="button"
            onClick={onBack}
          >
            <Icon name="back" size={16} />
            <span>All Courses</span>
          </button>

          <div className="vc-brand-row">
            <div className="vc-brand-mark">S</div>
            <div>
              <div className="vc-brand-title">
                Student Portal
              </div>
              <div className="vc-brand-subtitle">
                Course Space
              </div>
            </div>
          </div>
        </div>

        <div className="vc-course-identity">
          {course?.course_image_drive_file_id ? (
            <img
              src={`https://lh3.googleusercontent.com/d/${course.course_image_drive_file_id}`}
              alt=""
            />
          ) : (
            <div className="vc-course-identity-fallback">
              {courseName
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map(word => word[0])
                .join('')
                .toUpperCase()}
            </div>
          )}

          <div className="vc-course-identity-copy">
            <strong>{courseName}</strong>
            <span>{course?.semesters?.name}</span>
          </div>
        </div>

        {seriesContexts.length > 1 && (
          <div className="vc-series-semesters">
            <div className="vc-series-semesters-label">
              Semesters
            </div>

            {seriesContexts.map(
              (context, index) => {
                const linkedCourse =
                  context.course;

                const active =
                  String(
                    linkedCourse.id
                  ) ===
                  String(courseId);

                return (
                  <button
                    key={linkedCourse.id}
                    type="button"
                    className={
                      `vc-series-semester ${active ? 'active' : ''}`
                    }
                    onClick={() =>
                      switchSeriesOffering(
                        linkedCourse.id
                      )
                    }
                  >
                    <strong>
                      Semester {index + 1}
                    </strong>

                    <span>
                      {linkedCourse
                        ?.semesters
                        ?.name ||
                        'Semester'}
                    </span>
                  </button>
                );
              }
            )}
          </div>
        )}

        <nav
          className="vc-nav"
          aria-label="Course navigation"
        >
          {!onboardingInProgress && (
            <button
              type="button"
              className={
                `vc-nav-item ${!activeSection ? 'active' : ''}`
              }
              onClick={navigateBack}
            >
              <Icon name="dashboard" />
              <span>Course Dashboard</span>
            </button>
          )}

          {visibleNavigationItems.map(item => {
            const isActive =
              activeSection === item.key &&
              (
                item.key !== 'assignments' ||
                (
                  item.sequenceId
                    ? String(item.sequenceId) ===
                      String(selectedSequenceId || '')
                    : String(item.assignmentTypeId || '') ===
                      String(selectedAssignmentTypeId || '') &&
                      !selectedSequenceId
                )
              );

            return (
              <button
                key={
                  item.sequenceId ||
                  item.assignmentTypeId ||
                  item.key
                }

                type="button"
                className={
                  `vc-nav-item ${isActive ? 'active' : ''}`
                }
                onClick={() =>
                  navigateTo(
                    item.key,
                    item.assignmentTypeId || null,
                    item.sequenceId || null
                  )
                }
              >
                <Icon name={item.icon} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="vc-sidebar-footer">
          <button
            type="button"
            className="vc-signout"
            onClick={onLogout}
          >
            <Icon name="logout" size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
    );
  }


  function renderTopbar(title) {
    return (
      <header className="vc-topbar">
        <div>
          <div className="vc-topbar-kicker">
            {courseName}
          </div>
          <h1>{title}</h1>
        </div>

        {isOpenEverything(session) && (
          <div className="vc-testing-banner">
            Testing Mode — deadlines bypassed
          </div>
        )}

        <button
          type="button"
          className="vc-mobile-signout"
          onClick={onLogout}
        >
          Sign Out
        </button>
      </header>
    );
  }


  if (activeSection) {
    const sectionProps = {
      session,
      course,
      enrolment,
      onboarding,
      onBack: navigateBack,
      onOnboardingUpdate:
        handleOnboardingUpdate,
      onLogout,
      selectedAssignmentTypeId,
      selectedSequenceId,
      selectedSequenceName:
        assignmentSequences.find(
          sequence =>
            String(sequence.id) ===
            String(selectedSequenceId || '')
        )?.name || '',
      onOpenAssignmentType: typeId =>
        navigateTo(
          'assignments',
          typeId,
          selectedSequenceId
        ),
      onBackToSequence: selectedSequenceId
        ? () =>
            navigateTo(
              'assignments',
              null,
              selectedSequenceId
            )
        : null
    };

    const sectionTitles = {
      syllabus: 'Syllabus',
      schedule: 'Teaching Schedule',
      pd: 'Philosophical Diary',
      cq: 'Questions Assignment',
      qi: 'Question Improvement',
      assignments: 'Assignment',
      selfeval: 'Self-Evaluation',
      groups: 'Groups',
      documents: 'Documents',
      instructor: 'Instructor Information',
      voting: 'Class Voting',
      chat: 'Course Chat',
      progress: 'My Progress',
      checkin: 'Check In'
    };

    return (
      <div className="vc-shell">
        <style>{coursePortalCss}</style>

        {renderSidebar()}

        <main className="vc-main">
          {renderTopbar(
            sectionTitles[activeSection] ||
            'Course'
          )}

          <div className="vc-section-content">
            {activeSection === 'syllabus' && (
              <StudentSyllabus
                {...sectionProps}
              />
            )}

            {activeSection === 'schedule' && (
              seriesContexts.length > 1 ? (
                <div className="vc-series-schedule">
                  {seriesContexts.map(
                    (context, index) => (
                      <section
                        key={
                          context.course.id
                        }
                        className="vc-series-schedule-semester"
                      >
                        <div className="vc-series-schedule-heading">
                          <span>
                            Semester {index + 1}
                          </span>

                          <strong>
                            {context.course
                              ?.semesters
                              ?.name ||
                              'Semester'}
                          </strong>
                        </div>

                        <StudentSchedule
                          session={session}
                          course={
                            context.course
                          }
                          enrolment={
                            context.enrolment
                          }
                          hideHeading
                        />
                      </section>
                    )
                  )}
                </div>
              ) : (
                <StudentSchedule
                  {...sectionProps}
                />
              )
            )}

            {activeSection === 'pd' &&
              course?.pd_enabled !== false && (
                <StudentPd
                  {...sectionProps}
                />
              )}

            {activeSection === 'cq' &&
              course?.cq_enabled !== false && (
                <StudentCq
                  {...sectionProps}
                />
              )}

            {activeSection === 'qi' &&
              course?.cq_enabled !== false && (
                <StudentQi
                  session={session}
                  course={course}
                  enrolment={enrolment}
                  onBack={navigateBack}
                />
              )}

            {activeSection === 'assignments' && (
              <StudentAssignments
                {...sectionProps}
              />
            )}

            {activeSection === 'selfeval' && (
              <StudentSelfEval
                {...sectionProps}
              />
            )}

            {activeSection === 'groups' &&
              hasStudentGroups && (
                <StudentGroups
                  {...sectionProps}
                />
              )}

            {activeSection === 'readings' && (
              <StudentReadings
                course={course}
              />
            )}

            {activeSection === 'documents' && (
              <StudentDocuments
                {...sectionProps}
              />
            )}

            {activeSection === 'instructor' && (
              <StudentInstructor
                {...sectionProps}
              />
            )}

            {activeSection === 'voting' &&
              course?.voting_enabled === true && (
                <VotingDisplay
                  session={session}
                  course={course}
                />
              )}

            {activeSection === 'chat' && (
              <Chat
                session={session}
                initialCourseId={course.id}
                initialChatId={null}
                onBack={navigateBack}
              />
            )}

            {activeSection === 'progress' &&
              course
                ?.course_progress_enabled !==
                false && (
              <StudentProgress
                {...sectionProps}
                seriesContexts={
                  seriesContexts
                }
              />
            )}

            {activeSection === 'checkin' &&
              course?.attendance_enabled !== false && (
                <StudentCheckIn
                  session={session}
                  initialCourseId={courseId}
                  onBack={navigateBack}
                  embedded
                />
              )}
          </div>
        </main>
      </div>
    );
  }


  const pdDue = getNextPdDue();
  const cqDeadline = getNextCqDeadline();
  const upcomingAssignments =
    getUpcomingAssignments();

  const selfEvalInfo =
    getSelfEvalInfo().slice(0, 1);

  const dashboardItems = [];

  if (nextClassWeek) {
    dashboardItems.push({
      key: 'class',

      label:
        'Next class',

      value:
        formatDate(
          nextClassWeek
            .class_date
        ),

      sub: [
        nextClassTime,

        nextClassRoom
      ]
        .filter(Boolean)
        .join(' · ')
    });
  }

  if (
    course?.cq_enabled !== false &&
    (
      nextWeek?.cq_enabled ||
      isOpenEverything(session)
    ) &&
    cqDeadline
  ) {
    dashboardItems.push({
      key: 'cq',
      label: 'Questions Assignment',
      value: formatDate(
        cqDeadline.toISOString()
      ),
      sub:
        `Due ${getCqDueHoursBeforeClass()} ` +
        `${getCqDueHoursBeforeClass() === 1 ? 'hour' : 'hours'} before class`
    });
  }

  if (
    course?.pd_enabled !== false &&
    nextWeek?.pd_enabled &&
    pdDue
  ) {
    dashboardItems.push({
      key: 'pd',
      label: 'Philosophical Diary',
      value: formatDate(pdDue),
      sub: 'Next diary due'
    });
  }

  upcomingAssignments
    .forEach(a => {
      const requiresSubmission =
        a.assignment_types?.requires_submission ?? true;

      const today = getEffectiveToday();
      const effectiveOpenDate =
        getStudentAssignmentOpenDate(a);
      const effectiveDate =
        getStudentAssignmentDate(a);

      const openDate =
        effectiveOpenDate
          ? String(
              effectiveOpenDate
            ).slice(0, 10)
          : '';

      const isNotYetOpen =
        requiresSubmission &&
        openDate &&
        openDate > today &&
        !isOpenEverything(session);

      dashboardItems.push({
        key: a.id,
        label:
          a.structured_form_checkpoint
            ? a.label
            : (
                a.assignment_types?.name ||
                'Assignment'
              ),
        value: isNotYetOpen
          ? `Opens ${formatDate(
              effectiveOpenDate
            )}`
          : formatDate(effectiveDate),
        sub: isNotYetOpen
          ? `Due ${formatDate(
              effectiveDate
            )}`
          : a.structured_form_checkpoint &&
            a.schedule_source_label
            ? `Submission due · ${a.schedule_source_label}`
            : requiresSubmission
              ? 'Submission due'
              : 'Scheduled'
      });
    });

  selfEvalInfo.forEach((item, index) => {
    const isOpen = item.status === 'open';

    dashboardItems.push({
      key: `selfeval-${index}`,
      label: item.label,
      value: isOpen
        ? 'Open now'
        : formatDate(item.opens),
      sub: isOpen
        ? `Closes ${formatDate(item.closes)}`
        : 'Opens for self-evaluation',
      accent: isOpen
    });
  });


  return (
    <div className="vc-shell">
      <style>{coursePortalCss}</style>

      {renderSidebar()}

      <main className="vc-main">
        {renderTopbar('Course Dashboard')}

        <div className="vc-content">
          <section className="vc-hero">
            {course?.course_image_drive_file_id && (
              <img
                src={`https://lh3.googleusercontent.com/d/${course.course_image_drive_file_id}`}
                alt=""
                className="vc-hero-image"
              />
            )}

            <div className="vc-hero-overlay" />

            <div className="vc-hero-copy">
              <div className="vc-hero-semester">
                {course?.semesters?.name}
              </div>

              <h2>{courseName}</h2>

              <CoursePlacement
  session={session}
  enrolment={enrolment}
  variant="hero"
/>

              <div className="vc-hero-meta">
  {[
    Array.isArray(course?.class_days) && course.class_days.length
      ? course.class_days.map(day => {
          const time = course?.class_times?.[day] || (course?.class_days?.length === 1 ? course?.class_time : null);
          const room = course?.classrooms?.[day];
          return [day, time ? String(time).slice(0, 5) : null, room].filter(Boolean).join(' ');
        }).join(' · ')
      : course?.class_day
        ? [course.class_day, course?.class_time ? String(course.class_time).slice(0, 5) : null, course?.classrooms?.[course.class_day]].filter(Boolean).join(' ')
        : ''
  ].filter(Boolean).join('')}
</div>
            </div>
          </section>

          <div className="vc-dashboard-grid">
            <section className="vc-panel">
              <div className="vc-panel-heading">
                <div>
                  <div className="vc-panel-kicker">
                    At a glance
                  </div>
                  <h2>Upcoming</h2>
                </div>

                <Icon
                  name="clock"
                  size={20}
                />
              </div>

              {dashboardItems.length === 0 ? (
                <div className="vc-empty">
                  Nothing upcoming.
                </div>
              ) : (
                <div className="vc-upcoming-list">
                  {dashboardItems.map(item => (
                    <div
                      key={item.key}
                      className={
                        `vc-upcoming-item ${item.accent ? 'accent' : ''}`
                      }
                    >
                      <div>
                        <div className="vc-upcoming-label">
                          {item.label}
                        </div>

                        <div className="vc-upcoming-value">
                          {item.value}
                        </div>
                      </div>

                      {item.sub && (
                        <div className="vc-upcoming-sub">
                          {item.sub}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="vc-panel">
              <div className="vc-panel-heading">
                <div>
                  <div className="vc-panel-kicker">
                    Course updates
                  </div>
                  <h2>Announcements</h2>
                </div>

                <Icon
                  name="bell"
                  size={20}
                />
              </div>

              {notifications.length === 0 ? (
                <div className="vc-empty">
                  No announcements.
                </div>
              ) : (
                <div className="vc-notification-list">
                  {notifications.map(n => (
                    <div
                      key={n.id}
                      className="vc-notification"
                    >
                      <div className="vc-notification-message">
                        {n.message}
                      </div>

                      <div className="vc-notification-date">
                        {new Date(
                          n.created_at
                        ).toLocaleDateString(
                          'en-GB',
                          {
                            day: 'numeric',
                            month: 'short'
                          }
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}


const coursePortalCss = `
  * {
    box-sizing: border-box;
  }

  .vc-shell,
  .vc-loading-shell {
    min-height: 100vh;
    background: #f7f8fa;
    color: #192431;
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .vc-shell {
    display: flex;
  }

  .vc-sidebar {
    width: 266px;
    min-height: 100vh;
    height: 100vh;
    position: sticky;
    top: 0;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    background: #102a43;
    color: #fff;
  }

  .vc-brand {
    padding: 19px 20px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.09);
  }

  .vc-back-courses {
    border: 0;
    background: transparent;
    color: #9fb3c8;
    padding: 0;
    margin-bottom: 18px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
  }

  .vc-back-courses:hover {
    color: #fff;
  }

  .vc-brand-row {
    display: flex;
    align-items: center;
    gap: 11px;
  }

  .vc-brand-mark {
    width: 35px;
    height: 35px;
    display: grid;
    place-items: center;
    border: 1px solid rgba(255,255,255,0.5);
    font-family: Georgia, "Times New Roman", serif;
    font-size: 20px;
  }

  .vc-brand-title {
    font-family: Georgia, "Times New Roman", serif;
    font-size: 16px;
  }

  .vc-brand-subtitle {
    margin-top: 2px;
    color: #9fb3c8;
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.13em;
  }

  .vc-course-identity {
    display: flex;
    gap: 11px;
    align-items: center;
    padding: 18px 18px 17px;
    border-bottom: 1px solid rgba(255,255,255,0.09);
  }

  .vc-course-identity img,
  .vc-course-identity-fallback {
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    border-radius: 3px;
  }

  .vc-course-identity img {
    object-fit: cover;
    display: block;
  }

  .vc-course-identity-fallback {
    display: grid;
    place-items: center;
    background: #243b53;
    color: #d9e2ec;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 16px;
  }

  .vc-course-identity-copy {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .vc-course-identity-copy strong {
    color: #fff;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 13px;
    line-height: 1.25;
    font-weight: 500;
  }

  .vc-course-identity-copy span {
    color: #9fb3c8;
    font-size: 10px;
  }

  .vc-series-semesters {
    padding: 12px 12px 10px;
    border-bottom: 1px solid rgba(255,255,255,0.09);
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .vc-series-semesters-label {
    color: #829ab1;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin: 0 4px 2px;
  }

  .vc-series-semester {
    width: 100%;
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 4px;
    background: rgba(255,255,255,0.03);
    color: #c5d3df;
    padding: 8px 9px;
    text-align: left;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .vc-series-semester strong {
    font-size: 11px;
    color: inherit;
  }

  .vc-series-semester span {
    font-size: 9px;
    color: #9fb3c8;
  }

  .vc-series-semester.active {
    background: rgba(255,255,255,0.12);
    border-color: rgba(255,255,255,0.3);
    color: #fff;
  }

  .vc-series-schedule {
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  .vc-series-schedule-semester {
    min-width: 0;
  }

  .vc-series-schedule-heading {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin: 0 0 14px;
    padding-bottom: 9px;
    border-bottom: 1px solid #d9e2ec;
  }

  .vc-series-schedule-heading span {
    color: #2f6f9f;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .vc-series-schedule-heading strong {
    color: #243b53;
    font-size: 16px;
  }

  .vc-nav {
    padding: 15px 11px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    overflow-y: auto;
  }

  .vc-nav-item {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #c5d3df;
    padding: 9px 10px;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
  }

  .vc-nav-item:hover {
    color: #fff;
    background: rgba(255,255,255,0.07);
  }

  .vc-nav-item.active {
    color: #102a43;
    background: #fff;
  }

  .vc-sidebar-footer {
    margin-top: auto;
    padding: 14px 16px 18px;
    border-top: 1px solid rgba(255,255,255,0.09);
  }

  .vc-signout {
    width: 100%;
    border: 1px solid rgba(255,255,255,0.14);
    border-radius: 4px;
    background: transparent;
    color: #d9e2ec;
    padding: 8px 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
  }

  .vc-main {
    min-width: 0;
    flex: 1;
  }

  .vc-topbar {
    min-height: 94px;
    display: flex;
    align-items: center;
    gap: 20px;
    justify-content: space-between;
    padding: 22px 38px;
    background: #fff;
    border-bottom: 1px solid #e3e8ee;
  }

  .vc-topbar-kicker,
  .vc-panel-kicker,
  .vc-hero-semester {
    color: #627d98;
    text-transform: uppercase;
    font-size: 9px;
    letter-spacing: 0.13em;
    font-weight: 800;
  }

  .vc-topbar h1 {
    margin: 4px 0 0;
    color: #102a43;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 25px;
    line-height: 1.1;
    font-weight: 500;
  }

  .vc-testing-banner {
    margin-left: auto;
    background: #fff8e6;
    border: 1px solid #ecd8a6;
    color: #7c5b18;
    padding: 7px 10px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 800;
  }

  .vc-mobile-signout {
    display: none;
  }

  .vc-content,
  .vc-section-content {
    max-width: 1260px;
    margin: 0 auto;
    padding: 34px 38px 54px;
  }

  .vc-section-content {
    max-width: 1180px;
  }

  .vc-hero {
    min-height: 250px;
    position: relative;
    display: flex;
    align-items: flex-end;
    overflow: hidden;
    border-radius: 5px;
    background:
      linear-gradient(135deg, #102a43, #334e68);
    margin-bottom: 22px;
  }

  .vc-hero-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .vc-hero-overlay {
    position: absolute;
    inset: 0;
    background:
      linear-gradient(
        90deg,
        rgba(8, 29, 47, 0.88) 0%,
        rgba(8, 29, 47, 0.62) 48%,
        rgba(8, 29, 47, 0.18) 100%
      );
  }

  .vc-hero-copy {
    position: relative;
    z-index: 1;
    padding: 30px 32px;
    max-width: 720px;
  }

  .vc-hero-semester {
    color: #d9e2ec;
  }

  .vc-hero h2 {
    margin: 7px 0 8px;
    color: #fff;
    font-family: Georgia, "Times New Roman", serif;
    font-size: clamp(28px, 4vw, 42px);
    line-height: 1.05;
    font-weight: 500;
  }

  .vc-student-placement {
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    margin: -1px 0 7px;
  }

  .vc-hero-meta {
    color: #d9e2ec;
    font-size: 12px;
  }

  .vc-dashboard-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(300px, 0.9fr);
    gap: 22px;
  }

  .vc-panel {
    background: #fff;
    border: 1px solid #d9e2ec;
    border-radius: 5px;
    padding: 22px 24px;
  }

  .vc-panel-heading {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
    color: #627d98;
    padding-bottom: 14px;
    border-bottom: 1px solid #edf1f5;
  }

  .vc-panel-heading h2 {
    margin: 4px 0 0;
    color: #102a43;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 20px;
    font-weight: 500;
  }

  .vc-upcoming-item {
    min-height: 72px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 18px;
    padding: 14px 0;
    border-bottom: 1px solid #edf1f5;
  }

  .vc-upcoming-item:last-child {
    border-bottom: 0;
  }

  .vc-upcoming-item.accent {
    border-left: 2px solid #2f6f9f;
    padding-left: 12px;
  }

  .vc-upcoming-label {
    color: #627d98;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 9px;
    font-weight: 800;
    margin-bottom: 4px;
  }

  .vc-upcoming-value {
    color: #243b53;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 16px;
  }

  .vc-upcoming-sub {
    color: #829ab1;
    font-size: 10px;
    text-align: right;
    max-width: 130px;
  }

  .vc-notification {
    padding: 14px 0;
    border-bottom: 1px solid #edf1f5;
  }

  .vc-notification:last-child {
    border-bottom: 0;
  }

  .vc-notification-message {
    color: #334e68;
    font-size: 13px;
    line-height: 1.5;
  }

  .vc-notification-date {
    margin-top: 5px;
    color: #829ab1;
    font-size: 10px;
  }

  .vc-empty {
    color: #829ab1;
    font-size: 12px;
    padding: 18px 0 2px;
  }

  .vc-loading-shell {
    display: grid;
    place-items: center;
  }

  .vc-loading {
    color: #627d98;
    font-size: 13px;
  }

  /*
   * Existing course-section components still own their internal forms,
   * lists and functionality. These two rules only remove the old grey
   * page background so they sit naturally inside the new course shell.
   */
  .vc-section-content > div {
    background: transparent !important;
  }

  @media (max-width: 1000px) {
    .vc-dashboard-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 840px) {
    .vc-shell {
      display: block;
    }

    .vc-sidebar {
      width: 100%;
      height: auto;
      min-height: 0;
      position: static;
    }

    .vc-brand {
      padding: 14px 16px;
    }

    .vc-back-courses {
      margin-bottom: 12px;
    }

    .vc-course-identity {
      padding: 12px 16px;
    }

    .vc-course-identity img,
    .vc-course-identity-fallback {
      width: 42px;
      height: 42px;
    }

    .vc-nav {
      flex-direction: row;
      overflow-x: auto;
      padding: 8px 10px 11px;
    }

    .vc-nav-item {
      width: auto;
      min-width: max-content;
      padding: 9px 10px;
    }

    .vc-sidebar-footer {
      display: none;
    }

    .vc-topbar {
      min-height: 82px;
      padding: 18px 20px;
    }

    .vc-topbar h1 {
      font-size: 22px;
    }

    .vc-testing-banner {
      display: none;
    }

    .vc-mobile-signout {
      display: block;
      border: 1px solid #bcccdc;
      background: #fff;
      color: #334e68;
      border-radius: 4px;
      padding: 7px 10px;
      font-size: 10px;
      font-weight: 700;
      cursor: pointer;
    }

    .vc-content,
    .vc-section-content {
      padding: 24px 20px 42px;
    }

    .vc-hero {
      min-height: 220px;
    }

    .vc-hero-copy {
      padding: 24px;
    }
  }

  @media (max-width: 560px) {
    .vc-upcoming-item {
      align-items: flex-start;
      flex-direction: column;
      gap: 5px;
    }

    .vc-upcoming-sub {
      text-align: left;
      max-width: none;
    }

    .vc-topbar-kicker {
      max-width: 220px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;
